import type {
  IAnalyticsProperties,
  ICaptureAnalyticsEventProps,
  IIdentifyAnalyticsUserProps,
  IProductAnalyticsClient,
  ISetAnalyticsGroupProps,
} from "./product-analytics.types";
import { ANALYTICS_GROUP_TYPE_PROJECT } from "./product-analytics.types";

// NOTE: The web and mobile clients wrap two different PostHog SDKs, but the
// wrapping itself was identical in both: schedule the call, swallow anything it
// throws, log it for a developer and never for the user. Keeping two copies of
// that meant the never-break-a-user-flow guarantee had two places to regress
// independently — so it lives here once and each client supplies only what is
// genuinely platform-specific.
//
// The two real differences, and why they are parameters:
//
//   * `getSdk` — the SDK instance is a module singleton in each client, created
//     at a different moment (web loads it through a dynamic import), so it is
//     read per call rather than captured.
//   * `schedule` — web must buffer calls made before the SDK finishes loading;
//     mobile imports it statically and runs them straight away.

// NOTE: The minimum PostHog surface both SDKs satisfy. Deliberately not the
// SDKs' own types: this file is shared, and importing either one here would
// pull a platform SDK into the other platform's bundle.
export interface IProductAnalyticsSdk {
  capture: (event: string, properties?: IAnalyticsProperties) => unknown;
  identify: (
    distinctId: string,
    personProperties?: IAnalyticsProperties,
  ) => unknown;
  group: (
    groupType: string,
    groupKey: string,
    groupProperties?: IAnalyticsProperties,
  ) => unknown;
  reset: () => unknown;
}

export interface ILogAnalyticsFailureProps {
  action: string;
  error: unknown;
}

export interface ICreateProductAnalyticsClientProps {
  getSdk: () => IProductAnalyticsSdk | null;
  schedule: (call: () => void) => void;
  getBaseProperties: () => IAnalyticsProperties;
  logFailure: (props: ILogAnalyticsFailureProps) => void;
}

export interface IRunGuardedAnalyticsCallProps {
  action: string;
  run: () => void;
  logFailure: (props: ILogAnalyticsFailureProps) => void;
}

// NOTE: Exported so the reserved-event helpers each client owns — web's
// `$pageview`, mobile's `$screen` — get the same swallow-and-log treatment as
// the taxonomy events without repeating the try/catch a third and fourth time.
export const runGuardedAnalyticsCall = ({
  action,
  run,
  logFailure,
}: IRunGuardedAnalyticsCallProps): void => {
  try {
    run();
  } catch (error) {
    logFailure({ action, error });
  }
};

export const createProductAnalyticsClient = ({
  getSdk,
  schedule,
  getBaseProperties,
  logFailure,
}: ICreateProductAnalyticsClientProps): IProductAnalyticsClient => {
  const guard = ({ action, run }: { action: string; run: () => void }): void =>
    schedule(() => runGuardedAnalyticsCall({ action, run, logFailure }));

  return {
    capture: ({ event, properties }: ICaptureAnalyticsEventProps): void => {
      guard({
        action: `capture ${event}`,
        run: () =>
          void getSdk()?.capture(event, {
            ...getBaseProperties(),
            ...properties,
          }),
      });
    },

    identify: ({
      distinctId,
      personProperties,
    }: IIdentifyAnalyticsUserProps): void => {
      guard({
        action: "identify",
        // NOTE: Stitches the pre-login anonymous activity onto the real person,
        // which is what makes landing → signup → activation one funnel instead
        // of two disconnected ones.
        run: () => void getSdk()?.identify(distinctId, personProperties),
      });
    },

    setGroup: ({
      groupKey,
      groupProperties,
    }: ISetAnalyticsGroupProps): void => {
      guard({
        action: "setGroup",
        run: () =>
          void getSdk()?.group(
            ANALYTICS_GROUP_TYPE_PROJECT,
            groupKey,
            groupProperties,
          ),
      });
    },

    reset: (): void => {
      guard({
        action: "reset",
        // NOTE: Without this the next user on a shared machine inherits the
        // previous person's distinct_id and every per-user metric is corrupted.
        run: () => void getSdk()?.reset(),
      });
    },
  };
};
